import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { PolicyRulesIcon, TrashIcon } from "../Icons";
import { ColorBox } from "../LicensePolicy";
import CustomSelect from "../UI/CustomSelect";
import TableWithPagination from "./TableWithPagination";
import { PrimaryButton } from "@components/Buttons";
import SelectLicensePolicyExceptionRuleModal from "@components/Dialogs/SelectLicensePolicyExceptionRuleModal";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { toKebabCase } from "@utils/common";

// Reusable checkbox component to reduce duplication
const EditableCheckbox = ({ checked, onChange, colors }) => (
  <div className="flex items-start">
    <input
      type="checkbox"
      className="sr-only"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      tabIndex={-1}
    />
    <div
      className={`relative h-4 w-4 cursor-pointer rounded border-1 transition-all duration-200
				${checked
          ? `${colors.borderActiveDashed} ${colors.bgActivePrimary}`
          : `${colors.borderPrimary} ${colors.borderHoverPrimary}`
        }`}
      onClick={() => onChange(!checked)}
    >
      {checked && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg className={`h-3 w-3 ${colors.textOnPrimary}`} fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  </div>
);

// Reusable display checkbox component
const DisplayCheckbox = ({ checked, colors }) => (
  <div className="flex items-start">
    <span className={`text-sm ${colors.textPrimary}`}>{checked ? "✓" : "---"}</span>
  </div>
);

const LicensePolicyExceptionRuleTable = ({
  data = [],
  className = "",
  onDataChange = null,
  isEditMode = false,
  onSave = null,
  onAddRule = null,
  policySteps = {},
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [tableData, setTableData] = useState(data);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const { matchTypeList, fetchSearchInfo } = useSearchInfo();
  const displayData = useMemo(() => {
    return tableData.length > 0 ? tableData : data;
  }, [data, tableData]);

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  // Build step metadata from policySteps
  const { policyStepOptions, stepInfoByValue, defaultStepValue, defaultStepColor, defaultApproval } = useMemo(() => {
    // Sort by stepNumber instead of index
    const entries = Object.entries(policySteps).sort(([, a], [, b]) => a.stepNumber - b.stepNumber);

    // Remove duplicate step numbers and keep only unique ones
    const uniqueStepNumbers = [...new Set(entries.map(([, stepData]) => stepData.stepNumber))];

    const options = uniqueStepNumbers.map(stepNumber => {
      // Find the step with this stepNumber to get its color
      const stepWithNumber = Object.values(policySteps).find(stepData => stepData.stepNumber === stepNumber);
      return {
        value: `${stepNumber}`,
        label: `${stepNumber}`,
        color: stepWithNumber?.color || "black",
      };
    });

    const info = new Map(uniqueStepNumbers.map(stepNumber => {
      // Find the step with this stepNumber to get its data
      const stepWithNumber = Object.values(policySteps).find(stepData => stepData.stepNumber === stepNumber);
      return [`${stepNumber}`, {
        color: stepWithNumber?.color || "black",
        approval: stepWithNumber?.approval || "APPROVAL"
      }];
    }));

    const def = uniqueStepNumbers.length > 0 ? `${uniqueStepNumbers[0]}` : "";
    const defColor = def ? (info.get(def)?.color || "black") : "black";
    const defApproval = def ? (info.get(def)?.approval || "APPROVAL") : "APPROVAL";

    return { policyStepOptions: options, stepInfoByValue: info, defaultStepValue: def, defaultStepColor: defColor, defaultApproval: defApproval };
  }, [policySteps]);

  // Initialize table data when data changes and normalize defaults
  useEffect(() => {
    const normalized = (data || []).map((row, index) => {
      const policyStep = row.policyStep || defaultStepValue;
      const color = row.policyStepColor || (policyStep ? (stepInfoByValue.get(policyStep)?.color || defaultStepColor) : row.policyStepColor);
      const approvalCd = row.licenseUseApprovalCd || (policyStep ? (stepInfoByValue.get(policyStep)?.approval || defaultApproval) : row.licenseUseApprovalCd);
      return {
        ...row,
        policyStep,
        policyStepColor: color,
        licenseUseApprovalCd: approvalCd,
        uniqueRowId: row.uniqueRowId || `existing-${row.licensePolicyRuleSeq}-${index}` // Ensure existing data has unique ID
      };
    });
    setTableData(normalized);
  }, [data, defaultStepValue, defaultStepColor, defaultApproval]);

  // Use all data directly (no pagination)
  const allData = displayData;

  // Usage type options
  const usageTypeOptions = [{
    value: "N/A",
    label: t("common.usage-type.na"),
  }].concat(matchTypeList).map((item) => ({
    value: item.value,
    label: item.value === "N/A"
      ? t("common.usage-type.na")
      : item.label === 'etc'
        ? t("common.usage-type.etc")
        : t(`common.usage-type.${toKebabCase(item.label)}`),
  }));

  // License use approval options (display only in view mode)
  const approvalOptions = [
    { value: "APPROVAL", label: t("license-policy.approval") },
    { value: "DISAPPROVAL", label: t("license-policy.disapproval") },
    { value: "CONDITIONAL_APPROVAL", label: t("license-policy.conditional-approval") },
  ];

  const handleCheckboxChange = (rowId, field, value) => {
    const updatedData = (tableData.length > 0 ? tableData : data).map((row) =>
      (row.uniqueRowId || row.licensePolicyRuleSeq) === rowId ? { ...row, [field]: value } : row,
    );
    setTableData(updatedData);
    if (onDataChange) onDataChange(updatedData);
  };

  const handleSelectChange = (rowId, field, selectedOption) => {
    const updatedData = (tableData.length > 0 ? tableData : data).map((row) => {
      if ((row.uniqueRowId || row.licensePolicyRuleSeq) !== rowId) return row;
      if (field === "policyStep") {
        const newStep = selectedOption.value;
        const info = stepInfoByValue.get(newStep);
        return {
          ...row,
          policyStep: Number(newStep),
          policyStepColor: info?.color || row.policyStepColor,
          licenseUseApprovalCd: info?.approval || row.licenseUseApprovalCd,
        };
      }
      return { ...row, [field]: selectedOption.value };
    });
    setTableData(updatedData);
    if (onDataChange) onDataChange(updatedData);
  };

  const handleDeleteRule = (rowId) => {
    const updatedData = (tableData.length > 0 ? tableData : data).filter((row) => (row.uniqueRowId || row.licensePolicyRuleSeq) !== rowId);
    setTableData(updatedData);
    if (onDataChange) onDataChange(updatedData);
  };

  const openSelectModal = () => setShowSelectModal(true);
  const closeSelectModal = () => setShowSelectModal(false);

  const mapSelectedToRows = (selectedItems) => {
    return selectedItems.map((item) => ({
      licensePolicyRuleSeq: item.licenseAttributeSeq,
      licenseIdentifier: item.licenseIdentifier,
      licenseName: item.licenseName,
      matchTypeCd: "N/A",
      modificationFlag: "N",
      patentFlag: "N",
      policyStep: defaultStepValue,
      policyStepColor: defaultStepColor,
      licenseUseApprovalCd: defaultApproval,
    }));
  };

  const handleAddFromModal = (selectedItems) => {
    const newRows = mapSelectedToRows(selectedItems);
    const updated = [...(displayData || []), ...newRows];
    setTableData(updated);
    if (onDataChange) onDataChange(updated);
    closeSelectModal();
  };

  const initialSelectedForModal = useMemo(() => {
    return (displayData || [])
      .filter((row) => !!row.licensePolicyRuleSeq)
      .map((row) => ({
        licenseAttributeSeq: row.licensePolicyRuleSeq,
        licenseIdentifier: row.licenseIdentifier,
        licenseName: row.licenseName,
      }));
  }, [displayData]);

  const renderCheckbox = (row, field, isEditMode) => {
    const checked = row[field] === "Y" || row[field] === true;
    if (isEditMode) {
      return (
        <EditableCheckbox
          checked={checked}
          onChange={(newValue) => handleCheckboxChange(row.uniqueRowId || row.licensePolicyRuleSeq, field, newValue ? "Y" : "N")}
          colors={colors}
        />
      );
    }
    return <DisplayCheckbox checked={checked} colors={colors} />;
  };

  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case "licenseIdentifier":
        return <span className={`text-sm ${colors.textPrimary}`}>{row.licenseIdentifier || "---"}</span>;
      case "licenseName":
        return <span className={`text-sm ${colors.textPrimary}`}>{row.licenseName || "---"}</span>;
      case "matchTypeCd":
        if (isEditMode) {
          const currentValue = usageTypeOptions.find((option) => option.value === row.matchTypeCd);
          const valueToUse = currentValue || { value: "N/A", label: t("common.usage-type.na") };
          return (
            <div className="relative">
              <CustomSelect
                options={usageTypeOptions}
                value={valueToUse}
                onChange={(selectedOption) => handleSelectChange(row.uniqueRowId || row.licensePolicyRuleSeq, "matchTypeCd", selectedOption)}
                placeholder={t("common.please-select")}
                size="sm"
                className="w-full text-sm font-medium"
                menuPlacement="top"
                menuPosition="fixed"
                clearable={false}
              />
            </div>
          );
        }
        return (
          <span className={`text-sm ${colors.textPrimary}`}>
            {usageTypeOptions.find((o) => o.value === row.matchTypeCd)?.label || t("common.usage-type.na")}
          </span>
        );
      case "modificationFlag":
        return renderCheckbox(row, "modificationFlag", isEditMode);
      case "patentFlag":
        return renderCheckbox(row, "patentFlag", isEditMode);
      case "policyStep":
        if (isEditMode) {
          // Find the current value based on the actual row.policyStep, not defaulting to first option
          const currentValue = policyStepOptions.find((option) => option.value === row.policyStep);
          // If no match found, create a fallback option with the actual step value
          const valueToUse = currentValue || {
            value: row.policyStep || defaultStepValue,
            label: row.policyStep || defaultStepValue
          };
          return (
            <div className="relative">
              <CustomSelect
                options={policyStepOptions}
                value={valueToUse}
                onChange={(selectedOption) => handleSelectChange(row.uniqueRowId || row.licensePolicyRuleSeq, "policyStep", selectedOption)}
                placeholder={t("common.please-select")}
                size="sm"
                className="w-full text-sm font-medium"
                menuPlacement="top"
                menuPosition="fixed"
                clearable={false}
              />
            </div>
          );
        }
        return <span className={`text-sm ${colors.textPrimary}`}>{row.policyStep || "---"}</span>;
      case "obligationTypeColor":
        {
          const info = stepInfoByValue.get(row.policyStep);
          const colorName = info?.color || row.policyStepColor || defaultStepColor;
          return <ColorBox colorName={colorName} size="md" showLabel={false} />;
        }
      case "licenseUseApprovalCdName":
        {
          const currentOption = approvalOptions.find((option) => option.value === row.licenseUseApprovalCd);
          return <span className={`text-sm ${colors.textPrimary}`}>{currentOption ? currentOption.label : "---"}</span>;
        }
      case "actions":
        if (isEditMode) {
          return (
            <div className="flex items-center justify-center">
              <button onClick={() => handleDeleteRule(row.uniqueRowId || row.licensePolicyRuleSeq)} className={`${colors.iconDelete} ${colors.hoverTextDangerLight} transition-colors cursor-pointer`}>
                <TrashIcon size={16} />
              </button>
            </div>
          );
        }
        return <span className={`text-sm ${colors.textMuted}`}>---</span>;
      default:
        return <span className={`text-sm ${colors.textPrimary}`}>{row[columnKey] || "---"}</span>;
    }
  };

  const columns = [
    "licenseIdentifier",
    "licenseName",
    "matchTypeCd",
    "modificationFlag",
    "patentFlag",
    "policyStep",
    "obligationTypeColor",
    "licenseUseApprovalCdName",
    ...(isEditMode ? ["actions"] : []),
  ];

  const columnConfig = {
    licenseIdentifier: {
      title: t("license-policy.license-identifier"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "12%" }
    },
    licenseName: {
      title: t("license-policy.license-name"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "15%" }
    },
    matchTypeCd: {
      title: t("license-policy.match-type-cd-name"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "15%" },
      attributeTitle: false,
    },
    modificationFlag: {
      title: t("license-policy.modification-flag"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "10%" }
    },
    patentFlag: {
      title: t("license-policy.patent-flag"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "8%" }
    },
    policyStep: {
      title: t("license-policy.policy-step"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "12%" }
    },
    obligationTypeColor: {
      title: t("license-policy.obligation-type-color"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "12%" }
    },
    licenseUseApprovalCdName: {
      title: t("license-policy.license-use-approval"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "12%" }
    },
    actions: {
      title: t("common.action"),
      customRender: (v, r, k) => renderCell(r, k),
      style: { width: "8%" }
    }
  };

  if (isEditMode && Object.keys(policySteps).length === 0) {
    return (
      <div className={`${colors.bgTertiary} rounded-lg p-4 pt-10 pb-4 border ${colors.borderPrimary} ${className}`} data-testid="license-policy-exception-rule-table">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <PolicyRulesIcon size={56} className={colors.iconMuted} />
          </div>
          <div className={`${colors.textSubTitle} text-sm mb-4`}>{t("license-policy.please-add-step-first")}</div>
        </div>
      </div>
    );
  }

  if (isEditMode && displayData.length === 0) {
    return (
      <div className={`${colors.bgTertiary} rounded-lg p-4 border ${colors.borderPrimary} ${className}`} data-testid="license-policy-exception-rule-table">
        <table className="w-full table-fixed" style={{ tableLayout: 'fixed' }} data-testid="license-policy-exception-rule-table-table">
          <thead>
            <tr>
              {columns.map((column, index) => {
                const config = columnConfig[column] || {};
                const title = config.title || column;
                return (
                  <th key={index} className={`${colors.textSubTitle} px-4 py-4 text-left text-xs font-semibold tracking-wider overflow-hidden uppercase`} title={title} style={{ ...config.style, whiteSpace: config.style?.whiteSpace || 'nowrap', textOverflow: 'ellipsis' }}>
                    <div title={title}>{title}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-8" colSpan={columns.length}>
                <div className="text-center">
                  <div className="mb-4 flex justify-center">
                    <PolicyRulesIcon size={48} className={colors.iconMuted} />
                  </div>
                  <div className={`${colors.textSubTitle} text-sm mb-4`}>{t("license-policy.add-rule-description")}</div>
                  <PrimaryButton onClick={openSelectModal}>{t("license-policy.add-rule-button")}</PrimaryButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <SelectLicensePolicyExceptionRuleModal isOpen={showSelectModal} onClose={closeSelectModal} onAdd={handleAddFromModal} initialSelectedFiles={initialSelectedForModal} />
      </div>
    );
  }

  return (
    <div className={`${colors.bgPrimary} rounded-lg ${className}`} data-testid="license-policy-exception-rule-table">
      <TableWithPagination
        dataTestId="license-policy-exception-rule-table-grid"
        columns={columns}
        data={allData}
        colSize={12}
        columnConfig={columnConfig}
        rowKey="licensePolicyRuleSeq"
        tableStyle="default"
        showPagination={false}
      />

      {isEditMode && displayData.length > 0 && (
        <div className="flex justify-center mt-4 pb-4">
          <button type="button" data-testid="license-policy-exception-rule-table-add-rule-button" onClick={openSelectModal} className={`w-full inline-flex items-center justify-center px-6 py-2 border-2 border-dashed ${colors.borderOutLineButton} rounded-lg text-sm font-semibold ${colors.textActive} ${colors.bgAddVerificationFileBtn} transition-all duration-200 hover:opacity-80`}>
            + {t("license-policy.add-rule-button")}
          </button>
        </div>
      )}

      <SelectLicensePolicyExceptionRuleModal isOpen={showSelectModal} onClose={closeSelectModal} onAdd={handleAddFromModal} initialSelectedFiles={initialSelectedForModal} />
    </div>
  );
};

export default LicensePolicyExceptionRuleTable; 